import React, { useEffect, useRef, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  ChevronDown, ChevronUp, ExternalLink, FileSpreadsheet, Loader2, MoreVertical,
  Play, Plus, RefreshCw, Trash2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useAppState } from '../app/appContext';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { useT } from '@/contexts/I18nContext';
import {
  getRemindersSettings,
  patchRemindersSettings,
  initRemindersSettings,
  listChecklists,
  createChecklist,
  createChecklistFromTemplate,
  createManualChecklist,
  patchChecklist,
  deleteChecklist,
  syncChecklist,
  getChecklist,
  createChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  type ZoneRemindersSettingsDto,
  type ChecklistDto,
  type ChecklistItemDto,
} from '../api/ops_tasks';
import { extractApiError as extractError } from '../utils/apiError';
import { Tooltip } from '@/components/ui/tooltip';

// ─── helpers ──────────────────────────────────────────────────────────────────

const WEEKDAY_LABELS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
const ALL_DAYS_MASK = 0b1111111;

function formatWeekdays(mask: number): string {
  if (mask === ALL_DAYS_MASK) return 'каждый день';
  return WEEKDAY_LABELS.filter((_, i) => mask & (1 << i)).join(', ') || '—';
}

function formatSchedule(item: ChecklistItemDto): string {
  switch (item.schedule_type) {
    case 'weekly':  return formatWeekdays(item.weekdays_mask);
    case 'monthly': return `${item.month_day}-го числа`;
    case 'once':    return item.specific_date ?? '—';
    default:        return item.schedule_type;
  }
}

function formatAnswerFormat(raw: string): string {
  const map: Record<string, string> = { photo: 'фото', text: 'текст', video: 'видео', button: 'кнопка' };
  return raw.split(',').map(t => map[t.trim()] ?? t.trim()).join(' + ');
}

function pluralTasks(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return `${n} задача`;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return `${n} задачи`;
  return `${n} задач`;
}

function hhmm(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

// ─── Sync progress (Alexey #5911) ─────────────────────────────────────────────
// «Синхронизировать» used to answer 200 and nothing visibly happened. The BE now
// keeps sync_state (pending → running → ok/error); after the press we poll the
// checklist every 2 s until it settles and show where it is.

const SYNC_POLL_MS = 2000;
// The worker is rate-limited (12 syncs/min) — a queued sync can wait a bit,
// but not this long; past it we stop polling and say so.
const SYNC_POLL_GIVE_UP_MS = 3 * 60 * 1000;

function useChecklistSync(checklist: ChecklistDto, onRefresh: () => void) {
  const [live, setLive] = useState<ChecklistDto>(checklist);
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [justFinished, setJustFinished] = useState(false);
  useEffect(() => setLive(checklist), [checklist]);

  const inFlight = live.sync_state === 'pending' || live.sync_state === 'running';
  const startedMs = live.sync_started_at ? new Date(live.sync_started_at).getTime() : Date.now();
  const gaveUp = inFlight && Date.now() - startedMs > SYNC_POLL_GIVE_UP_MS;

  useEffect(() => {
    if (!inFlight || gaveUp) return;
    const id = window.setTimeout(async () => {
      try {
        const { data } = await getChecklist(live.id);
        setLive(data);
        if (data.sync_state === 'ok' || data.sync_state === 'error') {
          setJustFinished(true);
          onRefresh();
        }
      } catch {
        // transient — the next tick retries; the give-up timer still bounds it
        setLive(l => ({ ...l }));
      }
    }, SYNC_POLL_MS);
    return () => window.clearTimeout(id);
  }, [live, inFlight, gaveUp, onRefresh]);

  async function start() {
    setStarting(true);
    setErr(null);
    setJustFinished(false);
    try {
      const { data } = await syncChecklist(live.id);
      setLive(data);
    } catch (e) {
      setErr(extractError(e, 'Не удалось запустить синхронизацию.'));
    } finally {
      setStarting(false);
    }
  }

  let status: React.ReactNode = null;
  if (gaveUp) {
    status = <span className="text-muted-foreground">Синхронизация в очереди дольше обычного — обновите страницу через пару минут.</span>;
  } else if (live.sync_state === 'pending') {
    status = <span className="inline-flex items-center gap-1 text-muted-foreground"><Loader2 className="size-3 animate-spin" />Синхронизация в очереди…</span>;
  } else if (live.sync_state === 'running') {
    status = <span className="inline-flex items-center gap-1 text-muted-foreground"><Loader2 className="size-3 animate-spin" />Синхронизация…</span>;
  } else if (live.sync_state === 'ok' && live.sync_finished_at) {
    status = (
      <span className={justFinished ? 'text-green-600 dark:text-green-500' : 'text-muted-foreground'}>
        {justFinished ? 'Готово' : 'Синхронизировано'}: {pluralTasks(live.sync_items_count ?? 0)}, {hhmm(live.sync_finished_at)}
      </span>
    );
  } else if (live.sync_state === 'error' && live.sync_finished_at) {
    status = <span className="text-destructive">Ошибка синхронизации в {hhmm(live.sync_finished_at)}</span>;
  }

  return { busy: starting || (inFlight && !gaveUp), start, err, status, lastError: live.last_sync_error };
}

function isSync(cl: ChecklistDto): boolean {
  return !!cl.sheet_url;
}

// ─── Item form ────────────────────────────────────────────────────────────────

interface ItemFormData {
  due_time: string;
  task_text: string;
  weekdays_mask: number;
  schedule_type: 'weekly' | 'monthly' | 'once';
  month_day: number | null;
  specific_date: string | null;
  answer_format: string;
  active: boolean;
}

function defaultItemForm(): ItemFormData {
  return {
    due_time: '09:00',
    task_text: '',
    weekdays_mask: ALL_DAYS_MASK,
    schedule_type: 'weekly',
    month_day: null,
    specific_date: null,
    answer_format: 'photo,text',
    active: true,
  };
}

function itemToForm(item: ChecklistItemDto): ItemFormData {
  return {
    due_time: item.due_time.slice(0, 5),
    task_text: item.task_text,
    weekdays_mask: item.weekdays_mask,
    schedule_type: item.schedule_type,
    month_day: item.month_day,
    specific_date: item.specific_date,
    answer_format: item.answer_format,
    active: item.active,
  };
}

interface ItemFormProps {
  initial?: ItemFormData;
  saving?: boolean;
  onSave: (data: ItemFormData) => void;
  onCancel?: () => void;
  isEdit?: boolean;
}

function ItemForm({ initial, saving, onSave, onCancel, isEdit }: ItemFormProps) {
  const [form, setForm] = useState<ItemFormData>(initial ?? defaultItemForm());

  function toggleDay(bit: number) {
    setForm(f => ({ ...f, weekdays_mask: f.weekdays_mask ^ (1 << bit) }));
  }

  function toggleFormat(key: string) {
    setForm(f => {
      const parts = new Set(f.answer_format.split(',').map(s => s.trim()).filter(Boolean));
      if (parts.has(key)) parts.delete(key);
      else if (key === 'button') return { ...f, answer_format: 'button' }; // «кнопка» only on its own
      else { parts.delete('button'); parts.add(key); }
      return { ...f, answer_format: Array.from(parts).join(',') };
    });
  }

  const formats = new Set(form.answer_format.split(',').map(s => s.trim()).filter(Boolean));

  return (
    <div className="space-y-3 rounded-lg border border-dashed border-border p-3">
      <div className="flex gap-2 flex-wrap">
        <div className="grid gap-1">
          <Label className="text-xs">Время</Label>
          <Input
            className="w-24 text-sm"
            type="time"
            value={form.due_time}
            onChange={e => setForm(f => ({ ...f, due_time: e.target.value }))}
          />
        </div>
        <div className="flex-1 grid gap-1">
          <Label className="text-xs">Задача</Label>
          <Input
            className="text-sm"
            placeholder="Текст задачи..."
            value={form.task_text}
            onChange={e => setForm(f => ({ ...f, task_text: e.target.value }))}
          />
        </div>
      </div>

      <div className="grid gap-1">
        <Label className="text-xs">Периодичность</Label>
        <div className="flex gap-1">
          {(['weekly', 'monthly', 'once'] as const).map(type => (
            <button
              key={type}
              type="button"
              onClick={() => setForm(f => ({ ...f, schedule_type: type }))}
              className={cn(
                'rounded px-2 py-1 text-xs border transition-colors',
                form.schedule_type === type
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-muted-foreground hover:bg-muted',
              )}
            >
              {{ weekly: 'Еженедельно', monthly: 'Ежемесячно', once: 'Однократно' }[type]}
            </button>
          ))}
        </div>
      </div>

      {form.schedule_type === 'weekly' && (
        <div className="grid gap-1">
          <Label className="text-xs">Дни недели</Label>
          <div className="flex gap-1 flex-wrap">
            {WEEKDAY_LABELS.map((label, i) => (
              <button
                key={label}
                type="button"
                onClick={() => toggleDay(i)}
                className={cn(
                  'rounded px-2 py-1 text-xs border transition-colors',
                  form.weekdays_mask & (1 << i)
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background text-muted-foreground hover:bg-muted',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {form.schedule_type === 'monthly' && (
        <div className="grid gap-1">
          <Label className="text-xs">День месяца</Label>
          <Input
            className="w-24 text-sm"
            type="number"
            min={1}
            max={31}
            value={form.month_day ?? 1}
            onChange={e => setForm(f => ({ ...f, month_day: Math.min(31, Math.max(1, parseInt(e.target.value) || 1)) }))}
          />
        </div>
      )}

      {form.schedule_type === 'once' && (
        <div className="grid gap-1">
          <Label className="text-xs">Дата</Label>
          <Input
            className="w-40 text-sm"
            type="date"
            value={form.specific_date ?? ''}
            onChange={e => setForm(f => ({ ...f, specific_date: e.target.value || null }))}
          />
        </div>
      )}

      <div className="grid gap-1">
        <Label className="text-xs">Формат ответа</Label>
        <div className="flex gap-3">
          {(['photo', 'text', 'video', 'button'] as const).map(key => (
            <label key={key} className="flex items-center gap-1.5 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={formats.has(key)}
                onChange={() => toggleFormat(key)}
                className="cursor-pointer"
              />
              {{ photo: 'Фото', text: 'Текст', video: 'Видео', button: 'Кнопка «Сделано»' }[key]}
            </label>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={form.active}
          onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
          className="cursor-pointer"
        />
        Активна
      </label>

      <div className="flex gap-2">
        <Button size="sm" onClick={() => onSave(form)} disabled={saving || !form.task_text.trim()}>
          {saving && <Loader2 className="size-3 animate-spin mr-1" />}
          {isEdit ? 'Сохранить' : 'Добавить'}
        </Button>
        {onCancel && (
          <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>Отмена</Button>
        )}
      </div>
    </div>
  );
}

// ─── Manual checklist items ────────────────────────────────────────────────────

interface ManualItemsEditorProps {
  checklist: ChecklistDto;
  onRefresh: () => void;
}

/** Задача из адреса (`?item=<id>`, кнопка «изменить» в Статусе, #1438 п.2): сразу открываем её форму. */
function useWantedItemId(): number | null {
  const [params] = useSearchParams();
  const raw = params.get('item');
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function ManualItemsEditor({ checklist, onRefresh }: ManualItemsEditorProps) {
  const [adding, setAdding] = useState(false);
  const wanted = useWantedItemId();
  const [editingId, setEditingId] = useState<number | null>(() => (wanted != null && checklist.items.some(i => i.id === wanted && i.active) ? wanted : null));
  const wantedRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (editingId != null && editingId === wanted) wantedRef.current?.scrollIntoView({ block: 'center' });
    // Только при первом открытии по ссылке.
  }, []);
  const [saving, setSaving] = useState(false);

  async function handleAdd(data: ItemFormData) {
    setSaving(true);
    try {
      await createChecklistItem(checklist.id, {
        due_time: data.due_time + ':00',
        task_text: data.task_text,
        weekdays_mask: data.weekdays_mask,
        schedule_type: data.schedule_type,
        month_day: data.month_day,
        specific_date: data.specific_date,
        answer_format: data.answer_format,
        active: data.active,
      });
      setAdding(false);
      onRefresh();
    } catch { /* ignore */ } finally { setSaving(false); }
  }

  async function handleEdit(item: ChecklistItemDto, data: ItemFormData) {
    setSaving(true);
    try {
      await updateChecklistItem(checklist.id, item.id, {
        due_time: data.due_time + ':00',
        task_text: data.task_text,
        weekdays_mask: data.weekdays_mask,
        schedule_type: data.schedule_type,
        month_day: data.month_day,
        specific_date: data.specific_date,
        answer_format: data.answer_format,
        active: data.active,
      });
      setEditingId(null);
      onRefresh();
    } catch { /* ignore */ } finally { setSaving(false); }
  }

  async function handleDelete(item: ChecklistItemDto) {
    if (!window.confirm(`Удалить задачу «${item.task_text}»?`)) return;
    try {
      await deleteChecklistItem(checklist.id, item.id);
      onRefresh();
    } catch { /* ignore */ }
  }

  const active = checklist.items.filter(i => i.active);
  const inactive = checklist.items.filter(i => !i.active);

  return (
    <div className="space-y-2">
      {active.length === 0 && !adding && (
        <p className="text-xs italic text-muted-foreground">Нет задач — добавьте первую.</p>
      )}
      {active.map(item => (
        <div key={item.id} ref={item.id === wanted ? wantedRef : undefined}>
          {editingId === item.id ? (
            <ItemForm
              initial={itemToForm(item)}
              saving={saving}
              onSave={data => handleEdit(item, data)}
              onCancel={() => setEditingId(null)}
              isEdit
            />
          ) : (
            <div
              className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/30 group cursor-pointer"
              onClick={() => setEditingId(item.id)}
            >
              <span className="font-mono text-xs text-muted-foreground w-10 shrink-0">
                {item.due_time.slice(0, 5)}
              </span>
              <span className="text-xs text-muted-foreground w-24 shrink-0">{formatSchedule(item)}</span>
              <span className="flex-1 truncate">{item.task_text}</span>
              <span className="text-xs text-muted-foreground">{formatAnswerFormat(item.answer_format)}</span>
              <button
                type="button"
                onClick={e => { e.stopPropagation(); void handleDelete(item); }}
                className="invisible group-hover:visible text-destructive hover:text-destructive/80 ml-1"
              >
                <Trash2 className="size-3.5" />
              </button>
            </div>
          )}
        </div>
      ))}
      {inactive.length > 0 && (
        <p className="text-xs italic text-muted-foreground">+ {inactive.length} неактивных</p>
      )}
      {adding ? (
        <ItemForm saving={saving} onSave={handleAdd} onCancel={() => setAdding(false)} />
      ) : (
        <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
          <Plus className="size-3.5 mr-1" />
          Добавить задачу
        </Button>
      )}
    </div>
  );
}

// ─── Sync items list ──────────────────────────────────────────────────────────

/** Weekly first (by first weekday of the mask, «все» ahead of Monday), then
 *  monthly by day, then one-offs by date; within a day by due time. Synced
 *  rows used to come out in spreadsheet order, which is whatever the manager
 *  typed (Alexey 2026-09-08: «как попало отсортированы»). */
function scheduleSortKey(item: ChecklistItemDto): [number, string] {
  if (item.schedule_type === 'weekly') {
    const mask = item.weekdays_mask;
    if (mask === 127) return [-1, item.due_time];
    let first = 7;
    for (let d = 0; d < 7; d++) if (mask & (1 << d)) { first = d; break; }
    return [first, item.due_time];
  }
  if (item.schedule_type === 'monthly') return [100 + (item.month_day ?? 0), item.due_time];
  return [200, `${item.specific_date ?? ''} ${item.due_time}`];
}

function sortSyncItems(items: ChecklistItemDto[]): ChecklistItemDto[] {
  return [...items].sort((a, b) => {
    const [ka, ta] = scheduleSortKey(a);
    const [kb, tb] = scheduleSortKey(b);
    return ka - kb || ta.localeCompare(tb);
  });
}

/** Every Google-Sheets checklist starts paused (Alexey 2026-09-08); this is
 *  what the operator sees until they press «Запустить синхронизацию». Styled
 *  to match the plain empty-state hint below it (SyncItemsList) rather than
 *  a boxed callout — the two used to say near-identical things at once when
 *  the sheet was still empty, which read as duplicated text (Alexey 09.09). */
function SyncPausedHint() {
  return (
    <p className="text-xs italic text-muted-foreground">
      Подготовьте чек-лист в Google-таблице. Как только будете готовы, нажмите
      «Запустить синхронизацию» ниже — задачи придут в чат команды на следующий день.
    </p>
  );
}

function SyncItemsList({ items, paused = false }: { items: ChecklistItemDto[]; paused?: boolean }) {
  const active = sortSyncItems(items.filter(i => i.active));
  const inactive = items.filter(i => !i.active);

  if (items.length === 0) {
    // While paused, SyncPausedHint (rendered just above this) already says
    // "fill in the sheet and press sync" — nothing left to add here.
    if (paused) return null;
    return (
      <p className="text-xs italic text-muted-foreground">
        Пока нет задач — создайте их в Google Sheets по ссылке выше и нажмите «Синхронизировать».
      </p>
    );
  }

  return (
    <div className="space-y-0.5">
      {active.map(item => (
        <div key={item.id} className="flex items-baseline gap-2 px-1 py-0.5 text-sm rounded hover:bg-muted/20">
          <span className="font-mono text-xs text-muted-foreground w-10 shrink-0">{item.due_time.slice(0, 5)}</span>
          <span className="text-xs text-muted-foreground w-24 shrink-0">{formatSchedule(item)}</span>
          <span className="flex-1 truncate text-xs">{item.task_text}</span>
          <span className="text-xs text-muted-foreground shrink-0">{formatAnswerFormat(item.answer_format)}</span>
        </div>
      ))}
      {inactive.length > 0 && (
        <p className="text-xs italic text-muted-foreground mt-1">+ {inactive.length} неактивных</p>
      )}
    </div>
  );
}

// ─── Checklist card ───────────────────────────────────────────────────────────

// ─── Add-checklist dropdown — one button, two creation paths ────────────────

type CreateKind = 'sync' | 'manual';

function AddChecklistDropdown({
  onPick,
  buttonLabel = 'Добавить чек-лист',
  size = 'sm',
  variant = 'outline',
}: {
  onPick: (kind: CreateKind) => void;
  buttonLabel?: string;
  size?: 'sm' | 'default';
  variant?: 'outline' | 'default';
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const pick = (kind: CreateKind) => { setOpen(false); onPick(kind); };
  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        size={size}
        variant={variant}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="gap-1.5"
      >
        <Plus className="size-3.5" /> {buttonLabel}
      </Button>
      <Popover open={open} onOpenChange={setOpen} triggerRef={triggerRef} side="bottom" align="start">
        <div className="flex flex-col py-1 min-w-[16rem]" role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={() => pick('sync')}
            className="flex flex-col items-start gap-0.5 px-3 py-2 text-left text-sm transition-colors hover:bg-muted hover:text-foreground"
          >
            <span className="font-medium">Google Таблицы</span>
            <span className="text-xs text-muted-foreground">Заполняется в привязанной таблице по шаблону.</span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => pick('manual')}
            className="flex flex-col items-start gap-0.5 px-3 py-2 text-left text-sm transition-colors hover:bg-muted hover:text-foreground"
          >
            <span className="font-medium">Интерактивный</span>
            <span className="text-xs text-muted-foreground">Задачи редактируются прямо здесь.</span>
          </button>
        </div>
      </Popover>
    </>
  );
}

// ─── Inline-editable title ────────────────────────────────────────────────────

function EditableTitle({
  value,
  onSave,
  className,
  saving,
}: {
  value: string;
  onSave: (next: string) => Promise<void> | void;
  className?: string;
  saving?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  async function commit() {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== value) {
      await onSave(next);
    } else {
      setDraft(value);
    }
  }

  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        disabled={saving}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); void commit(); }
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
        className={cn('h-9 text-base font-semibold', className)}
      />
    );
  }
  return (
    <Tooltip content="Нажмите, чтобы переименовать"><button
      type="button"
      onClick={() => setEditing(true)}
      className={cn('group text-left text-base font-semibold leading-tight transition-colors hover:text-primary', className)}
    >
      {value || '—'}
    </button></Tooltip>
  );
}

// ─── Sheets-sync card (lives inside SingleChecklistView) ─────────────────────

function SheetSyncCard({
  checklist,
  onRefresh,
  onDelete,
}: {
  checklist: ChecklistDto;
  onRefresh: () => void;
  onDelete?: () => void;
}) {
  const menuRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sheetUrl, setSheetUrl] = useState(checklist.sheet_url);
  const [worksheetTitle, setWorksheetTitle] = useState(checklist.worksheet_title);
  const syncOn = checklist.sync_on;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setSheetUrl(checklist.sheet_url);
    setWorksheetTitle(checklist.worksheet_title);
  }, [checklist]);

  async function handleEditSave() {
    setBusy(true);
    setErr(null);
    try {
      await patchChecklist(checklist.id, {
        sheet_url: sheetUrl,
        worksheet_title: worksheetTitle,
      });
      setEditing(false);
      onRefresh();
    } catch (e) {
      setErr(extractError(e, 'Не удалось сохранить.'));
    } finally {
      setBusy(false);
    }
  }

  async function toggleSync() {
    setMenuOpen(false);
    setBusy(true);
    setErr(null);
    try {
      await patchChecklist(checklist.id, { sync_on: !syncOn });
      onRefresh();
    } catch (e) {
      setErr(extractError(e, 'Не удалось изменить синхронизацию.'));
    } finally {
      setBusy(false);
    }
  }

  async function detachFromSheets() {
    setMenuOpen(false);
    if (!window.confirm('Отвязать чек-лист от Google Таблицы? Задачи сохранятся, но синхронизация остановится.')) return;
    setBusy(true);
    setErr(null);
    try {
      await patchChecklist(checklist.id, { sheet_url: '', sync_on: false });
      onRefresh();
    } catch (e) {
      setErr(extractError(e, 'Не удалось отвязать.'));
    } finally {
      setBusy(false);
    }
  }

  // Single uniform-typography line: "Собирается из Google Таблицы ·
  // <worksheet>". Whole row clickable to open the sheet in a new
  // tab. We don't store the file's display name (БЕ never exposed
  // it), so the worksheet title doubles as the at-a-glance label.
  const line = (
    <>
      <FileSpreadsheet className="mt-px size-3.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 truncate">
        Собирается из Google Таблицы
        {checklist.worksheet_title && <> · {checklist.worksheet_title}</>}
      </span>
      {!syncOn && (
        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">пауза</span>
      )}
      {checklist.sheet_url && <ExternalLink className="size-3 shrink-0 text-muted-foreground" />}
    </>
  );

  return (
    <div className="rounded-md border border-border bg-muted/30">
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
        {checklist.sheet_url ? (
          <Tooltip content={checklist.sheet_url}><a
            href={checklist.sheet_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-w-0 flex-1 items-center gap-2 hover:text-foreground"
          >
            {line}
          </a></Tooltip>
        ) : (
          <span className="flex min-w-0 flex-1 items-center gap-2">{line}</span>
        )}
        <Tooltip content="Действия"><button
          ref={menuRef}
          type="button"
          onClick={() => setMenuOpen(o => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className="-mr-1 inline-flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Действия"
        >
          <MoreVertical className="size-3.5" />
        </button></Tooltip>
        <Popover open={menuOpen} onOpenChange={setMenuOpen} triggerRef={menuRef} side="bottom" align="end">
          <div className="flex flex-col py-1 min-w-[14rem]" role="menu">
            <button
              role="menuitem"
              type="button"
              onClick={() => { setMenuOpen(false); setEditing(true); }}
              className="px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted hover:text-foreground"
            >
              Изменить ссылку
            </button>
            <button
              role="menuitem"
              type="button"
              onClick={toggleSync}
              disabled={busy}
              className="px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              {syncOn ? 'Поставить на паузу' : 'Включить синхронизацию'}
            </button>
            <button
              role="menuitem"
              type="button"
              onClick={detachFromSheets}
              disabled={busy}
              className="px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              Отвязать от Google Таблицы
            </button>
            {onDelete && (
              <>
                <div className="my-1 h-px bg-border" />
                <button
                  role="menuitem"
                  type="button"
                  onClick={() => { setMenuOpen(false); onDelete(); }}
                  disabled={busy}
                  className="px-3 py-1.5 text-left text-sm text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
                >
                  Удалить чек-лист
                </button>
              </>
            )}
          </div>
        </Popover>
      </div>

      {editing && (
        <div className="space-y-2 border-t border-border bg-background/60 p-3">
          <div className="grid gap-1">
            <Label className="text-xs">Название листа</Label>
            <Input
              value={worksheetTitle}
              onChange={e => setWorksheetTitle(e.target.value)}
              className="h-9 text-sm"
            />
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">URL таблицы</Label>
            <Input
              value={sheetUrl}
              onChange={e => setSheetUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/..."
              className="h-9 font-mono text-sm"
            />
          </div>
          {err && <p className="text-xs text-destructive">{err}</p>}
          <div className="flex gap-2">
            <Button size="sm" onClick={handleEditSave} disabled={busy}>
              {busy && <Loader2 className="mr-1 size-3 animate-spin" />}
              Сохранить
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={busy}>
              Отмена
            </Button>
          </div>
        </div>
      )}
      {!editing && err && <p className="px-3 pb-2 text-xs text-destructive">{err}</p>}
    </div>
  );
}

// ─── Single-checklist view (the common case after #5 spec) ───────────────────

function SingleChecklistView({
  checklist,
  onRefresh,
  onAdd,
}: {
  checklist: ChecklistDto;
  onRefresh: () => void;
  onAdd: (kind: CreateKind) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const sheetSync = useChecklistSync(checklist, onRefresh);
  const syncing = sheetSync.busy;

  const sync = isSync(checklist);

  async function handleRename(next: string) {
    setRenaming(true);
    setErr(null);
    try {
      await patchChecklist(checklist.id, { worksheet_title: next });
      onRefresh();
    } catch (e) {
      setErr(extractError(e, 'Не удалось переименовать.'));
    } finally {
      setRenaming(false);
    }
  }

  const handleSync = sheetSync.start;

  async function handleDelete() {
    if (!window.confirm('Удалить чек-лист и все его задачи?')) return;
    setDeleting(true);
    setErr(null);
    try {
      await deleteChecklist(checklist.id);
      onRefresh();
    } catch (e) {
      setErr(extractError(e, 'Не удалось удалить.'));
      setDeleting(false);
    }
  }

  return (
    <Card variant="section">
      <CardContent className="space-y-4">
        {/* Header: editable checklist name + (single) sheets card. */}
        <EditableTitle value={checklist.worksheet_title} onSave={handleRename} saving={renaming} />

        {sync && <SheetSyncCard checklist={checklist} onRefresh={onRefresh} onDelete={handleDelete} />}

        {/* Tasks (sync or manual). */}
        {sync && !checklist.sync_on && <SyncPausedHint />}
        {sync ? (
          <SyncItemsList items={checklist.items} paused={!checklist.sync_on} />
        ) : (
          <ManualItemsEditor checklist={checklist} onRefresh={onRefresh} />
        )}

        {sync && sheetSync.status && <p className="text-xs">{sheetSync.status}</p>}
        {sheetSync.lastError && (
          <p className="text-xs text-destructive">Ошибка синхронизации: {sheetSync.lastError}</p>
        )}
        {(err || sheetSync.err) && <p className="text-xs text-destructive">{err || sheetSync.err}</p>}

        {/* Bottom action row. Delete is intentionally absent in the
            single-checklist case (it gets cluttered) — for sync-mode
            checklists Delete lives in the SheetSyncCard's 3-dots
            menu. Manual-mode singletons keep a small ghost-link
            Delete at the end of the row as a last-resort affordance. */}
        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
          {sync && (checklist.sync_on ? (
            <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing}>
              {syncing ? <Loader2 className="mr-1 size-3 animate-spin" /> : <RefreshCw className="mr-1 size-3" />}
              Синхронизировать
            </Button>
          ) : (
            // Paused → one blue call to action; the BE's sync endpoint also
            // switches sync_on on, so this press starts syncing for good.
            <Button size="sm" onClick={handleSync} disabled={syncing}>
              {syncing ? <Loader2 className="mr-1 size-3 animate-spin" /> : <Play className="mr-1 size-3" />}
              Запустить синхронизацию
            </Button>
          ))}
          <AddChecklistDropdown onPick={onAdd} />
          {!sync && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="ml-auto text-xs text-muted-foreground hover:text-destructive disabled:opacity-50"
            >
              {deleting ? 'Удаление…' : 'Удалить чек-лист'}
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface ChecklistCardProps {
  checklist: ChecklistDto;
  onRefresh: () => void;
  /** When true, render without the collapsible header — the parent
   *  already wraps a single-checklist case in its own card; no need
   *  for nested collapse chrome. */
  flat?: boolean;
}

function ChecklistCard({ checklist, onRefresh, flat = false }: ChecklistCardProps) {
  const wanted = useWantedItemId();
  const [expanded, setExpanded] = useState(flat || (wanted != null && checklist.items.some(i => i.id === wanted)));
  const [editing, setEditing] = useState(false);
  const [sheetUrl, setSheetUrl] = useState(checklist.sheet_url);
  const [worksheetTitle, setWorksheetTitle] = useState(checklist.worksheet_title);
  const [syncOn, setSyncOn] = useState(checklist.sync_on);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sheetSync = useChecklistSync(checklist, onRefresh);
  const syncing = sheetSync.busy;

  const sync = isSync(checklist);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await patchChecklist(checklist.id, {
        worksheet_title: worksheetTitle,
        ...(sync && { sheet_url: sheetUrl, sync_on: syncOn }),
      });
      setEditing(false);
      onRefresh();
    } catch (e) {
      setError(extractError(e, 'Не удалось сохранить.'));
    } finally {
      setSaving(false);
    }
  }

  const handleSync = sheetSync.start;

  async function handleDelete() {
    if (!window.confirm('Удалить чек-лист и все его задачи?')) return;
    setDeleting(true);
    try {
      await deleteChecklist(checklist.id);
      onRefresh();
    } catch (e) {
      setError(extractError(e, 'Не удалось удалить.'));
      setDeleting(false);
    }
  }

  return (
    <div className={cn(!flat && 'rounded-lg border border-border overflow-hidden')}>
      {!flat && (
        <div
          className="flex items-center gap-2 px-3 py-2 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => setExpanded(e => !e)}
        >
          {expanded ? <ChevronUp className="size-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="size-4 shrink-0 text-muted-foreground" />}
          <span className="flex-1 text-sm font-medium truncate">
            {checklist.worksheet_title}
            {sync ? (
              <span className="ml-2 text-xs font-normal text-muted-foreground">• Google Sheets</span>
            ) : (
              <span className="ml-2 text-xs font-normal text-muted-foreground">• Ручной</span>
            )}
          </span>
          {checklist.last_sync_error && (
            <span className="text-xs text-destructive shrink-0">Ошибка синхр.</span>
          )}
          <span className="text-xs text-muted-foreground shrink-0">{checklist.items.length} задач</span>
        </div>
      )}

      {expanded && (
        <div className={cn('space-y-3', !flat && 'px-3 py-3')}>
          {sync && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              <FileSpreadsheet className="size-4 shrink-0 text-foreground" />
              <span className="text-foreground">Задачи берутся из Google Sheets.</span>
              <span>Чтобы изменить — отредактируйте таблицу и нажмите «Синхронизировать».</span>
              {checklist.sheet_url && (
                <a
                  href={checklist.sheet_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
                  onClick={e => e.stopPropagation()}
                >
                  Открыть таблицу <ExternalLink className="size-3" />
                </a>
              )}
            </div>
          )}

          {editing ? (
            <div className="space-y-2">
              <div className="grid gap-1">
                <Label className="text-xs">Название</Label>
                <Input
                  className="text-xs"
                  value={worksheetTitle}
                  onChange={e => setWorksheetTitle(e.target.value)}
                />
              </div>
              {sync && (
                <>
                  <div className="grid gap-1">
                    <Label className="text-xs">URL таблицы</Label>
                    <Input
                      className="text-xs font-mono"
                      value={sheetUrl}
                      onChange={e => setSheetUrl(e.target.value)}
                      placeholder="https://docs.google.com/spreadsheets/..."
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={syncOn}
                      onChange={e => setSyncOn(e.target.checked)}
                      className="cursor-pointer"
                    />
                    Авто-синхронизация включена
                  </label>
                </>
              )}
              {error && <p className="text-xs text-destructive">{error}</p>}
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="size-3 animate-spin mr-1" />}
                  Сохранить
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving}>
                  Отмена
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>Переименовать</Button>
              {sync && (checklist.sync_on ? (
                <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing}>
                  {syncing ? <Loader2 className="size-3 animate-spin mr-1" /> : <RefreshCw className="size-3 mr-1" />}
                  {syncing ? 'Синхр...' : 'Синхронизировать'}
                </Button>
              ) : (
                <Button size="sm" onClick={handleSync} disabled={syncing}>
                  {syncing ? <Loader2 className="size-3 animate-spin mr-1" /> : <Play className="size-3 mr-1" />}
                  Запустить синхронизацию
                </Button>
              ))}
              <Button size="sm" variant="destructive" onClick={handleDelete} disabled={deleting}>
                {deleting ? <Loader2 className="size-3 animate-spin mr-1" /> : <Trash2 className="size-3 mr-1" />}
                Удалить
              </Button>
              {(error || sheetSync.err) && <p className="text-xs text-destructive self-center">{error || sheetSync.err}</p>}
              {sync && sheetSync.status && <p className="w-full text-xs">{sheetSync.status}</p>}
            </div>
          )}

          {sync && !checklist.sync_on && <SyncPausedHint />}
          {sync ? (
            <SyncItemsList items={checklist.items} paused={!checklist.sync_on} />
          ) : (
            <ManualItemsEditor checklist={checklist} onRefresh={onRefresh} />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Add-checklist dialog (sync mode needs a URL upfront) ────────────────────

function AddSheetDialog({
  open,
  zoneId,
  onClose,
  onCreated,
}: {
  open: boolean;
  zoneId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  // Two ways in (Alexey 2026-09-08): the branch already keeps its checklist in
  // a spreadsheet (Igor's spots share one file) → paste the URL; or we copy the
  // standard template for them («Чек-лист <точка>», first sheet «Рабочий
  // чек-лист») and they fill it in.
  const [mode, setMode] = useState<'existing' | 'template'>('template');
  const [sheetUrl, setSheetUrl] = useState('');
  const [worksheetTitle, setWorksheetTitle] = useState('Sheet1');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMode('template');
      setSheetUrl('');
      setWorksheetTitle('Sheet1');
      setError(null);
    }
  }, [open]);

  async function handleCreate() {
    setSaving(true);
    setError(null);
    try {
      if (mode === 'template') {
        await createChecklistFromTemplate({ zone: zoneId });
      } else {
        await createChecklist({
          zone: zoneId,
          sheet_url: sheetUrl.trim(),
          worksheet_title: worksheetTitle.trim() || 'Sheet1',
          // Starts paused — «Запустить синхронизацию» on the card turns it on.
          sync_on: false,
        });
      }
      onCreated();
      onClose();
    } catch (e) {
      setError(extractError(e, 'Не удалось создать чек-лист.'));
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="mb-3 text-base font-semibold">Чек-лист из Google Таблиц</h3>
        <div className="space-y-3">
          <div className="grid gap-1.5" role="radiogroup" aria-label="Откуда взять таблицу">
            {([
              ['template', 'Создать новую таблицу из стандартного шаблона', 'Мы скопируем шаблон, назовём его «Чек-лист <точка>» и откроем доступ по ссылке. Заполнять — на листе «Рабочий чек-лист».'],
              ['existing', 'У меня уже есть таблица с чек-листом', 'Укажите ссылку на неё и лист — подойдёт и общая таблица на несколько точек.'],
            ] as const).map(([value, title, hint]) => (
              <label
                key={value}
                className={cn(
                  'flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
                  mode === value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
                )}
              >
                <input
                  type="radio"
                  name="sheet-mode"
                  className="mt-1"
                  checked={mode === value}
                  onChange={() => setMode(value)}
                />
                <span className="flex flex-col gap-0.5">
                  <span className="font-medium">{title}</span>
                  <span className="text-xs text-muted-foreground">{hint}</span>
                </span>
              </label>
            ))}
          </div>
          {mode === 'existing' && (
            <>
              <div className="grid gap-1">
                <Label className="text-xs">URL таблицы</Label>
                <Input
                  autoFocus
                  placeholder="https://docs.google.com/spreadsheets/..."
                  value={sheetUrl}
                  onChange={e => setSheetUrl(e.target.value)}
                  className="h-9 font-mono text-sm"
                />
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">Название листа</Label>
                <Input
                  value={worksheetTitle}
                  onChange={e => setWorksheetTitle(e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            </>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose} disabled={saving}>Отмена</Button>
          <Button size="sm" onClick={handleCreate} disabled={saving || (mode === 'existing' && !sheetUrl.trim())}>
            {saving && <Loader2 className="mr-1 size-3 animate-spin" />}
            {mode === 'template' ? 'Создать таблицу и чек-лист' : 'Создать'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Reminders section ────────────────────────────────────────────────────────

// checklists_enabled is the feature flag, not a reminders/deadline setting —
// keep it out of this form's draft so it isn't rendered as a toggle here.
type RemindersDraft = Omit<ZoneRemindersSettingsDto, 'id' | 'zone' | 'checklists_enabled'>;

function RemindersSection({ zoneId }: { zoneId: string }) {
  const swrKey = `ops_tasks:reminders:${zoneId}`;
  const { data, isLoading, error: fetchError, mutate } = useSWR<ZoneRemindersSettingsDto | null>(
    swrKey,
    async () => {
      try {
        const resp = await getRemindersSettings(zoneId);
        return resp.status === 204 ? null : resp.data;
      } catch (e: unknown) {
        if (typeof e === 'object' && e !== null && 'response' in e && (e as any).response?.status === 404) return null;
        throw e;
      }
    },
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  const [draft, setDraft] = useState<RemindersDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (data) setDraft({
      deadline_pre_reminder_minutes: data.deadline_pre_reminder_minutes,
      overdue_minutes: data.overdue_minutes,
      send_pre_deadline_message: data.send_pre_deadline_message,
      send_deadline_message: data.send_deadline_message,
      send_overdue_message: data.send_overdue_message,
      ops_tasks_chat_id: data.ops_tasks_chat_id,
      ops_tasks_thread_id: data.ops_tasks_thread_id,
    });
  }, [data]);

  async function handleInit() {
    setSaving(true);
    try {
      await initRemindersSettings(zoneId);
      void mutate();
    } catch (e) {
      setError(extractError(e, 'Ошибка инициализации.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await patchRemindersSettings(zoneId, draft);
      setSuccess(true);
      void mutate();
    } catch (e) {
      setError(extractError(e, 'Не удалось сохранить настройки.'));
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) return <div className="flex items-center gap-1.5 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Загрузка...</div>;
  if (fetchError && data === undefined) return <ServerStatusNotice variant="card" error={fetchError} onRetry={() => mutate()} />;

  if (!data || !draft) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">Настройки уведомлений не инициализированы.</p>
        <Button size="sm" onClick={handleInit} disabled={saving}>
          {saving && <Loader2 className="size-3 animate-spin mr-1" />}
          Инициализировать
        </Button>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  return <RemindersBody draft={draft} setDraft={setDraft} saving={saving} success={success} error={error} onSave={handleSave} />;
}

function RemindersBody({
  draft,
  setDraft,
  saving,
  success,
  error,
  onSave,
}: {
  draft: RemindersDraft;
  setDraft: React.Dispatch<React.SetStateAction<RemindersDraft | null>>;
  saving: boolean;
  success: boolean;
  error: string | null;
  onSave: () => void;
}) {
  // chat_id + thread_id collapsed by default — operators only touch
  // them at onboarding. The timing fields and per-event toggles are
  // the day-to-day knobs and stay visible.
  const [chatExpanded, setChatExpanded] = useState(false);
  const togglesData: Array<[keyof RemindersDraft, string]> = [
    ['send_pre_deadline_message', 'Напоминание до дедлайна'],
    ['send_deadline_message', 'Сообщение в дедлайн'],
    ['send_overdue_message', 'Сообщение о просрочке'],
  ];
  return (
    <div className="space-y-4">
      {/* Two-column layout on wide screens: timing inputs on one side,
          per-event toggles on the other. Mobile stacks them in a
          single column. */}
      <div className="grid gap-3 sm:grid-cols-2 sm:gap-6">
        {/* Number inputs — narrow (w-20) since two digits is the
            typical input. */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3 text-sm">
            <Label className="text-xs text-muted-foreground">Напоминание до дедлайна, мин</Label>
            <Input
              type="number"
              min={0}
              className="h-8 w-20 text-sm"
              value={draft.deadline_pre_reminder_minutes}
              onChange={e => setDraft(d => d && ({ ...d, deadline_pre_reminder_minutes: Number(e.target.value) }))}
            />
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <Label className="text-xs text-muted-foreground">Просрочка, мин</Label>
            <Input
              type="number"
              min={0}
              className="h-8 w-20 text-sm"
              value={draft.overdue_minutes}
              onChange={e => setDraft(d => d && ({ ...d, overdue_minutes: Number(e.target.value) }))}
            />
          </div>
        </div>

        {/* Per-event toggles — inline Switch + label rows; no
            bordered chrome per row since they sit in a tight column. */}
        <div className="space-y-2">
          {togglesData.map(([key, label]) => (
            <label key={key} className="flex items-center justify-between gap-3 text-sm">
              <span>{label}</span>
              <Switch
                checked={!!draft[key]}
                onCheckedChange={(v) => setDraft(d => d && ({ ...d, [key]: v }))}
              />
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-md border border-border">
        <button
          type="button"
          onClick={() => setChatExpanded(v => !v)}
          className="flex w-full items-center justify-between px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-muted/40"
        >
          <span>Telegram-чат (ID и тема)</span>
          {chatExpanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </button>
        {chatExpanded && (
          <div className="grid gap-3 border-t border-border p-3 sm:grid-cols-2">
            <div className="grid gap-1">
              <Label className="text-xs">ID чата Telegram</Label>
              <Input
                className="font-mono text-sm"
                value={draft.ops_tasks_chat_id ?? ''}
                onChange={e => setDraft(d => d && ({ ...d, ops_tasks_chat_id: e.target.value || null }))}
                placeholder="-100123456789"
              />
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">ID темы (thread)</Label>
              <Input
                className="font-mono text-sm"
                value={draft.ops_tasks_thread_id ?? ''}
                onChange={e => setDraft(d => d && ({ ...d, ops_tasks_thread_id: e.target.value || null }))}
                placeholder="1234"
              />
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}
      {success && <p className="text-xs text-success">Сохранено.</p>}
      <Button size="sm" onClick={onSave} disabled={saving}>
        {saving && <Loader2 className="size-3 animate-spin mr-1" />}
        Сохранить
      </Button>
    </div>
  );
}

// ─── Root ──────────────────────────────────────────────────────────────────────

const ChecklistsSettings: React.FC = () => {
  const { t } = useT();
  const { zone } = useAppState();
  const zoneId = zone.selectedZoneId;

  const swrKey = zoneId ? `ops_tasks:checklists:${zoneId}` : null;
  const { data, isLoading, error, mutate } = useSWR<ChecklistDto[]>(
    swrKey,
    () => listChecklists(zoneId!).then(r => r.data.results),
    { shouldRetryOnError: false },
  );

  const [addSheetOpen, setAddSheetOpen] = useState(false);

  if (!zoneId) {
    return <BranchRequiredState />;
  }

  const checklists = data ?? [];

  // Add flow: dropdown picks 'sync' or 'manual'. Manual creates
  // immediately with a placeholder title (operator renames inline);
  // sync opens a small modal to collect the URL up-front because
  // the BE's sheet_url field is non-blank.
  async function handleAdd(kind: CreateKind) {
    if (!zoneId) return;
    if (kind === 'manual') {
      try {
        await createManualChecklist({ zone: zoneId, worksheet_title: 'Новый чек-лист' });
        void mutate();
      } catch {
        // The single-checklist view's error surface picks this up on
        // refresh; no need for a separate top-level toast.
      }
    } else {
      setAddSheetOpen(true);
    }
  }

  return (
    <div className="space-y-4">
      {/* Checklists card first — operators look at the checklist
          content far more often than they touch notification
          settings. Notifications-card moved below per #5 spec. */}
      {checklists.length === 1 ? (
        // Single-checklist case: card header IS the checklist name;
        // SingleChecklistView owns the full layout (editable title +
        // sheets card + tasks + bottom action row).
        <SingleChecklistView
          checklist={checklists[0]}
          onRefresh={() => void mutate()}
          onAdd={handleAdd}
        />
      ) : (
        <Card>
          <CardContent className="space-y-3">
            <h2 className="mb-3 text-base font-semibold">Чек-листы</h2>

            {isLoading && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Загрузка...
              </div>
            )}
            {error && !isLoading && !data && (
              <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />
            )}

            {!isLoading && (!error || !!data) && (
              <div className="space-y-2">
                {checklists.map(cl => (
                  <ChecklistCard
                    key={cl.id}
                    checklist={cl}
                    onRefresh={() => void mutate()}
                    flat={false}
                  />
                ))}
                {checklists.length === 0 && (
                  <p className="text-sm text-muted-foreground">Чек-листы не настроены.</p>
                )}
                <div className="pt-1">
                  <AddChecklistDropdown onPick={handleAdd} />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Notifications card last. */}
      <Card>
        <CardContent className="space-y-3">
          <h2 className="mb-3 text-base font-semibold">Уведомления</h2>
          <RemindersSection zoneId={zoneId} />
        </CardContent>
      </Card>

      <AddSheetDialog
        open={addSheetOpen}
        zoneId={zoneId}
        onClose={() => setAddSheetOpen(false)}
        onCreated={() => void mutate()}
      />
    </div>
  );
};

export default ChecklistsSettings;
